import { useState } from "react";
import { useNavigate } from "react-router";
import { SITUACAO_DO_EXTRATO_LABELS } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { reais } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import { AcoesDoExtrato } from "@/components/cobrancas/AcoesDoExtrato";
import { COR_DA_SITUACAO_DO_EXTRATO, dataCurta, envioDoExtrato, mesDoExtrato } from "./situacao";

const moldura = "overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card";
const ABERTOS = new Set(["EM_ABERTO", "PARCIAL", "VENCIDO"]);

/** Os extratos emitidos — por padrão os que ainda têm saldo; "Todos" mostra quitados e cancelados. */
export function ExtratosEmitidos() {
  const lista = trpc.financeiro.extratos.listar.useQuery().data;
  const navigate = useNavigate();
  const [todos, setTodos] = useState(false);
  const visiveis = (lista ?? []).filter((e) => todos || ABERTOS.has(e.situacao));

  return (
    <section className={moldura}>
      <header className="flex items-center justify-between gap-2 border-b border-zinc-100 bg-zinc-50/60 px-4 py-2 dark:border-border dark:bg-muted/30">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Extratos emitidos</h3>
        <div className="inline-flex rounded-md border border-zinc-200 bg-white p-0.5 text-xs dark:border-border dark:bg-card">
          {[false, true].map((v) => (
            <button
              key={String(v)}
              type="button"
              aria-pressed={todos === v}
              onClick={() => setTodos(v)}
              className={cn("rounded px-2.5 py-1", todos === v ? "bg-brand-600 font-semibold text-white" : "text-zinc-600 hover:bg-zinc-50 dark:text-muted-foreground")}
            >
              {v ? "Todos" : "Em aberto"}
            </button>
          ))}
        </div>
      </header>
      {lista && visiveis.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-zinc-500 dark:text-muted-foreground">
          {todos ? "Nenhum extrato emitido ainda." : "Nenhum extrato em aberto."}
        </p>
      ) : (
        <ul className="divide-y divide-zinc-100 dark:divide-border/50">
          {visiveis.map((e) => (
            <li key={e.id} className="flex items-center gap-1 pr-2 hover:bg-zinc-50/60 dark:hover:bg-muted/40">
              <button
                type="button"
                onClick={() => navigate(`/financeiro/extratos/${e.id}`)}
                className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1 py-3 pl-4 text-left"
              >
                <span className="w-12 text-xs font-semibold tabular-nums text-zinc-500">Nº {e.numero}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{e.destinatarioNome}</span>
                  <span className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                    Mês {mesDoExtrato(e.rotulo)} · vence {dataCurta(e.vencimento)} · total {reais(e.totalCents)}
                    {e.recebidoCents > 0 && ` · recebido ${reais(e.recebidoCents)}`}
                  </span>
                </span>
                {e.status === "EMITIDO" && (
                  <span className={cn("hidden w-40 text-right text-[11px] sm:inline", e.enviadoEm ? "text-zinc-500 dark:text-muted-foreground" : "font-semibold text-amber-600")}>
                    {envioDoExtrato(e)}
                  </span>
                )}
                <span className={cn("rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase", COR_DA_SITUACAO_DO_EXTRATO[e.situacao])}>
                  {SITUACAO_DO_EXTRATO_LABELS[e.situacao]}
                </span>
                <span className="w-28 text-right text-sm font-semibold tabular-nums text-zinc-900 dark:text-foreground">{reais(Math.max(0, e.saldoCents))}</span>
              </button>
              <AcoesDoExtrato extrato={e} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
